import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

/**
 * Admin forgot-password request page (admin_reset scope).
 * Mirrors the tenant flow at /forgot but for platform admins on
 * admin.dealerbosses.com — distinct route + endpoint, same anti-
 * enumeration behavior: always show the same confirmation panel on
 * submit regardless of whether the email matches an admin row.
 */
export function Forgot() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      // /auth/forgot always returns 200 — we don't branch on response.
      // Catch + swallow so a network error still lands the user on the
      // same confirmation panel (anti-enumeration via the loud path too).
      await api('/auth/forgot', { method: 'POST', json: { email } });
    } catch {
      /* same outcome */
    }
    setDone(true);
    setSubmitting(false);
  }

  return (
    <div className="login-page">
      <div className="card login-card">
        <h1>Forgot your admin password?</h1>
        <p className="muted small">
          Enter the email you sign into admin.dealerbosses.com with. If there's
          an active admin on file, we'll send a reset link valid for 1 hour.
        </p>
        {done ? (
          <div style={{ marginTop: 16 }}>
            <p>
              If <strong>{email}</strong> matches an admin account, a reset
              link has been sent. Check your inbox (and spam folder).
            </p>
            <p className="muted small">
              Didn't get it within a few minutes? Make sure the address is the
              one you signed up with, or contact support.
            </p>
            <div style={{ marginTop: 16 }}>
              <Link to="/login">← Back to sign in</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} autoComplete="off">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
            />
            <div className="button-row" style={{ marginTop: 16 }}>
              <button type="submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send reset link'}
              </button>
            </div>
            <div style={{ marginTop: 16, fontSize: 13 }}>
              <Link to="/login">← Back to sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
